// Lightweight client-side telemetry — wraps window.logAudit (defined in
// app.js) so we reuse the existing audit sink instead of opening a parallel
// writer. Routes to tenants/{tid}/auditLog with category='telemetry' so
// admin UI (Clock → Health → Audit Log) can filter on category.
//
// Closes the "did the user actually try this?" observability gap surfaced
// during the 2026-05-04 North Point/Rhyder SMS investigation, where Cloud
// Function logs proved "no successful sendMassText since Apr 13" but
// couldn't tell us whether Rhyder had attempted to click — the action could
// have been swallowed client-side before any network request fired.
//
// No firebase-config import here — that would mismatch the rest of the
// codebase's ?v= cache buster and instantiate duplicate module state.
// window.logAudit handles auth, tenant resolution, and timestamp.
//
// ══ WHAT BELONGS IN THIS SINK, AND WHAT MUST NEVER (TASK-9251) ══
//
// This is `tenants/{tid}/auditLog` — a real collection with real reads, real
// cost and a 90-day TTL, shared with the business audit trail an operator
// reads for compliance. So the bar is not "is this interesting", it is:
//
//   ONLY log the moments the app DECLINES to do what the user asked, or does
//   something DIFFERENT from what it told them. NEVER the happy path.
//
// A listener on every click buries the five rows that matter and makes the
// audit log useless for the one thing it exists for. Sample nothing; log
// rarely. If an event would not, on its own, replace a support conversation
// that starts with "can you try it again while I watch?", it does not go here.
//
// Before adding one, check it is not ALREADY covered — this repo instruments
// errors well and successes well, and the gap is narrow:
//   • a thrown exception            → window.logError (debugLogs + errorLogs)
//   • an uncaught error / rejection → app.js window.onerror / onunhandledrejection
//   • a completed business action   → window.logAudit('ticket'|'clock'|…, …)
// What NONE of those catch is the case that made this task: nothing threw, the
// UI said success, and the database disagrees.
//
// 🔴 PII: `context` is free-form and lands in a collection an admin can read.
// Never a driver PIN, a phone number, an email address, a document URL, or a
// person's NAME where an id would do. A SERVER error string is not exempt —
// a callable that rethrows `"… " + error.message` can carry a provider's echo
// of the recipient address, so run any error text through `scrubPii` below
// before it goes in.

function logUserAction(action, context = {}) {
  window.logAudit?.('telemetry', action, {
    type: 'user_action',
    context,
    url: location.pathname + location.hash,
    appVersion: window.APP_VERSION || null,
  });
}

// Same as logUserAction, but writes at most ONE row per `key` for the life of
// the page. USE THIS for any divergence that is a property of a RECORD rather
// than of a click — the state is still wrong on the tenth open, but the tenth
// row tells support nothing the first did not, and a hundred of them bury the
// events that do. Measured, not assumed: 102 live TITS tickets are invoiced
// AND carry a 0-hour clocked line, so an un-deduped "heal could not persist"
// would write a row every time a biller opened any of them.
//
// KNOWN LIMIT: the key is marked BEFORE the write, and `logAudit` is
// fire-and-forget, so a write that fails at the network suppresses the retry.
// That is the deliberate trade — a diagnostic must never retry-storm the sink
// it is protecting. Tenant is folded into the key so a tenant switch inside one
// page session cannot collide on a shared record id. Returns true if it wrote.
const _onceKeys = new Set();
function logUserActionOnce(key, action, context = {}) {
  const k = `${window.AppState?.tenant || '?'}:${action}:${key}`;
  if (_onceKeys.has(k)) return false;
  _onceKeys.add(k);
  logUserAction(action, context);
  return true;
}

// Strip the things that must never reach this collection out of a string we do
// not control — chiefly a SERVER error message, which on the invoice path is
// literally `"Failed to send invoice email: " + error.message` and can carry a
// provider's echo of the recipient address. Emails and long digit runs (phone /
// card-shaped) become placeholders; the diagnostic value of the message is in
// its SHAPE, never in the identifier inside it.
function scrubPii(str, max = 200) {
  return String(str == null ? '' : str)
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/\+?\d[\d\s().-]{8,}\d/g, '[number]')
    .slice(0, max);
}

// fix/boot-watchdog-deadend — the index.html boot watchdog cannot write to
// Firestore (it fires precisely because the app is not up), so it parks a stall
// record in localStorage 'dd.bootStall' and the next session_start carries it:
// WHICH boot step was pending when it fired. Read-and-clear, whitelisted fields
// only — step names are fixed literals from the boot code, never user data.
// Records older than 7 days are dropped rather than attributed to this session.
function takeBootStall() {
  let raw = null;
  try { raw = localStorage.getItem('dd.bootStall'); } catch (_e) { return null; }
  if (!raw) return null;
  try { localStorage.removeItem('dd.bootStall'); } catch (_e) { /* best effort */ }
  try {
    const r = JSON.parse(raw);
    const at = Number(r.at) || 0;
    const age = Date.now() - at;
    if (!at || age < 0 || age > 7 * 24 * 3600 * 1000) return null;
    const str = (v, n) => String(v == null ? '' : v).replace(/[^A-Za-z0-9:>_.-]/g, '').slice(0, n);
    const num = (v) => (Number.isFinite(Number(v)) ? Math.round(Number(v)) : null);
    return {
      kind: str(r.kind, 20),
      step: str(r.step, 40),
      stepAgeMs: num(r.stepAgeMs),
      elapsedMs: num(r.elapsedMs),
      trail: str(r.trail, 400),
      sinceStallMs: num(age),
    };
  } catch (_e) {
    return null;
  }
}

function logSessionStart() {
  const bootStall = takeBootStall();
  window.logAudit?.('telemetry', 'session_start', {
    type: 'session_start',
    url: location.pathname + location.hash,
    userAgent: navigator.userAgent.slice(0, 200),
    appVersion: window.APP_VERSION || null,
    ...(bootStall ? { bootStall } : {}),
  });
}

window.Telemetry = { logUserAction, logUserActionOnce, logSessionStart, scrubPii };
